import { Component, computed, HostListener, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { CLARIFICATION_TYPE, Relative } from '../../../../api/app-types/relative.app';
import { CarouselElement, NavCarousel } from '../../../../core/components/nav-carousel/nav-carousel';
import { registerSvgIcon } from '../../../../shared/helpers/icon-register.func';
import { ClientRelativesAnonymizationDialog } from '../client-relatives-anonymization-dialog/client-relatives-anonymization-dialog';
import { DatePipe } from '@angular/common';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { ContactPointType } from '../../../../api/app-types/contact-point.app';
import { NeighborContext } from '../../../../api/app-types/neighbors.app';

@Component({
	selector: 'lvr-client-relatives-detail',
	templateUrl: './client-relatives-detail.html',
	styleUrl: './client-relatives-detail.scss',
	imports: [TranslocoPipe, MatIcon, NavCarousel, DatePipe, LvrHeading],
})
export class ClientRelativesDetail {
	private readonly router = inject(Router);
	private readonly activeRoute = inject(ActivatedRoute);
	private readonly data = toSignal(this.activeRoute.data);
	private readonly dialog = inject(MatDialog);
	private readonly session = inject(SessionManagerService);
	private readonly clientId = computed(() => this.data()?.['clientId'] as number);
	private readonly relativeNeighbors = computed(() => this.data()?.['relativeNeighbors'] as NeighborContext<Relative>);
	public readonly carouselElements = computed(() => {
		const neighbors = this.relativeNeighbors();

		if (!neighbors) return [];

		return [neighbors.previous, neighbors.current, neighbors.next]
			.filter((person): person is Relative => !!person)
			.map((person) => ({
				id: person.id.toString(),
				label: person.firstName ? `${person.lastName}, ${person.firstName} `.trim() : `${person.lastName ?? ''}`.trim(),
			}));
	});

	public readonly currentIndex = computed(() => {
		const person = this.relative();
		const elements = this.carouselElements();

		if (!person || elements.length === 0) return 0;

		const index = elements.findIndex((e) => e.id === person.id.toString());

		return index >= 0 ? index : 0;
	});
	public readonly backLink = computed(() =>
		this.router.createUrlTree(['../'], { relativeTo: this.activeRoute }).toString(),
	);
	public readonly relative = computed(() => this.data()?.['relative'] as Relative);
	public readonly preferredCommunicationWays = computed(
		() => this.relative().contactPoints?.filter((cp) => cp.preferredWayOfContact) ?? [],
	);
	public readonly otherCommunicationWays = computed(
		() => this.relative().contactPoints?.filter((cp) => !cp.preferredWayOfContact) ?? [],
	);
	public readonly name = computed(() =>
		this.relative().firstName
			? `${this.relative().lastName}, ${this.relative().firstName}`.trim()
			: `${this.relative().lastName ?? ''}`.trim(),
	);
	public readonly showGhostIcon = signal(false);
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly canEditClientEnvironments = hasPermission(this.session.permissions, 'ClientEnvironments.Edit');
	public readonly canDeleteClientEnvironments = hasPermission(this.session.permissions, 'ClientEnvironments.Delete');

	public readonly clarificationTypes = [
		{ value: CLARIFICATION_TYPE.PHONE, label: 'telefonisch' },
		{ value: CLARIFICATION_TYPE.IN_PERSON, label: 'persönlich' },
		{ value: CLARIFICATION_TYPE.MAIL, label: 'schriftlich / email' },
	];

	public get CONTACT_POINT_TYPE() {
		return ContactPointType;
	}

	public constructor() {
		registerSvgIcon('ghost', '/assets/icons/ghost.svg');
	}

	public getClarificationLabel(id?: CLARIFICATION_TYPE) {
		const type = this.clarificationTypes.find((t) => t.value === id);
		return type ? type.label : '';
	}

	/**
	 * Handles the navigation from the nav carousel.
	 * Updates the route based on the selected element.
	 */
	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.router.navigate(['../', event.element.id], { relativeTo: this.activeRoute });
	}

	/**
	 * Opens the anonymization dialog for the current relative.
	 */
	public async openAnonymizationDialog() {
		if (!this.canDeleteClientEnvironments()) return;

		await lastValueFrom(
			this.dialog
				.open(ClientRelativesAnonymizationDialog, {
					width: '600px',
					data: {
						relative: this.relative(),
						clientId: this.clientId(),
						onAnonymized: async () => {
							await this.router.navigate(['../'], { relativeTo: this.activeRoute });
						},
					},
				})
				.afterClosed(),
		);
	}

	public openEdit() {
		this.router.navigate(['edit'], { relativeTo: this.activeRoute });
	}

	/**
	 * Hidden feature, don't tell anyone! ;)
	 */
	@HostListener('window:keydown', ['$event'])
	public onKeyDown(event: KeyboardEvent) {
		if (event.key.toLowerCase() === 'shift' && event.code.toLowerCase() === 'shiftleft') {
			this.showGhostIcon.set(true);
		}
	}

	@HostListener('window:keyup', ['$event'])
	public onKeyUp(event: KeyboardEvent) {
		if (event.key.toLowerCase() === 'shift' && event.code.toLowerCase() === 'shiftleft') {
			this.showGhostIcon.set(false);
		}
	}
}
